import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, Plus, X, Play, RefreshCw, Check, KeyRound, AlertCircle } from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { api, ApiError } from "../lib/api";

type Method = "GET" | "POST";
type Visibility = "customer" | "internal";

interface KV { name: string; value: string }
/** Response-mapping row carries visibility + optional label in addition to
 *  the (name, path) pair. Default visibility on new rows is "internal" so
 *  tenants opt-in to customer exposure explicitly. */
interface MappingRow { name: string; path: string; visibility: Visibility; label?: string }

interface ConnectorState {
  connectorId?: string;
  displayName: string;
  description: string;
  method: Method;
  baseUrl: string;
  pathTemplate: string;
  queryParams: KV[];
  staticHeaders: KV[];
  bodyTemplate: string;
  vars: KV[];
  authHeaderName: string;
  authHeaderValuePrefix: string;
  responseMapping: MappingRow[];
  timeoutMs: number;
  hasSecret?: boolean;
}

const EMPTY: ConnectorState = {
  displayName: "",
  description: "",
  method: "GET",
  baseUrl: "https://",
  pathTemplate: "/",
  queryParams: [],
  staticHeaders: [{ name: "Accept", value: "application/json" }],
  bodyTemplate: "",
  vars: [],
  authHeaderName: "Authorization",
  authHeaderValuePrefix: "Bearer ",
  responseMapping: [],
  timeoutMs: 10000,
};

export default function ConnectorEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [state, setState] = useState<ConnectorState>(EMPTY);
  const [authSecret, setAuthSecret] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    type ServerMappingEntry = string | { path: string; visibility?: Visibility; label?: string };
    api.get<ConnectorState & {
      vars: Record<string, string>;
      queryParams: KV[];
      staticHeaders: KV[];
      responseMapping: Record<string, ServerMappingEntry>;
    }>(`/api/connectors/${id}`)
      .then((c) => {
        setState({
          ...c,
          // Server stores vars / responseMapping as records; we edit as arrays.
          vars: Object.entries(c.vars ?? {}).map(([name, value]) => ({ name, value })),
          responseMapping: Object.entries(c.responseMapping ?? {}).map(([name, entry]) => {
            // Legacy rows are plain strings — default to "internal" so we don't
            // suddenly expose previously-private fields.
            if (typeof entry === "string") {
              return { name, path: entry, visibility: "internal" as Visibility };
            }
            return {
              name,
              path: entry.path ?? "",
              visibility: entry.visibility === "customer" ? "customer" : "internal",
              ...(entry.label ? { label: entry.label } : {}),
            };
          }),
        });
      })
      .catch(() => setError("Connector not found"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const patch = (p: Partial<ConnectorState>) => setState((s) => ({ ...s, ...p }));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const body = serialize(state, authSecret);
      if (isNew) {
        const created = await api.post<ConnectorState & { connectorId: string }>("/api/connectors", body);
        setAuthSecret("");
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
        navigate(`/connectors/${created.connectorId}`, { replace: true });
      } else {
        const updated = await api.patch<ConnectorState & { connectorId: string }>(`/api/connectors/${id}`, body);
        setState((s) => ({ ...s, hasSecret: updated.hasSecret ?? s.hasSecret }));
        setAuthSecret("");
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      }
    } catch (err) {
      setError((err as ApiError).message ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const destroy = async () => {
    if (isNew) return;
    if (!confirm("Delete this connector? Any category linked to it will fall back to no live data.")) return;
    try {
      await api.del(`/api/connectors/${id}`);
      navigate("/connectors", { replace: true });
    } catch (err) {
      setError((err as ApiError).message ?? "Delete failed.");
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <Link to="/connectors" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-3.5 w-3.5" /> All connectors
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            {isNew ? "New connector" : state.displayName || "(unnamed)"}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Defines an HTTP request the AI runs when this connector is linked to a category.
          </p>
        </div>
        {!isNew && (
          <Button variant="danger" size="sm" onClick={destroy}>
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
        <div className="space-y-6">
          {/* Identity */}
          <Card>
            <CardHeader title="Identity" description="What this connector is and who uses it." />
            <CardBody className="space-y-4">
              <Input label="Name" placeholder="Acme Order Lookup" value={state.displayName} onChange={(e) => patch({ displayName: e.target.value })} />
              <Input label="Description (optional)" placeholder="Fetches order status + ETA from Acme's order API." value={state.description} onChange={(e) => patch({ description: e.target.value })} />
            </CardBody>
          </Card>

          {/* Request */}
          <Card>
            <CardHeader title="Request" description="HTTP request the AI will send. Use {{slots.X}}, {{vars.X}}, {{customer.email}}, {{case.id}} in any field." />
            <CardBody className="space-y-4">
              <div className="grid grid-cols-[120px_1fr] gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900">Method</label>
                  <select
                    value={state.method}
                    onChange={(e) => patch({ method: e.target.value as Method })}
                    className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-mono"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </div>
                <Input
                  label="Base URL"
                  placeholder="https://api.acme.com"
                  value={state.baseUrl}
                  onChange={(e) => patch({ baseUrl: e.target.value })}
                  hint="https:// only. No userinfo. Public hostname (private IPs are blocked)."
                />
              </div>
              <Input
                label="Path template"
                placeholder="/v1/orders/{{slots.order_id}}"
                value={state.pathTemplate}
                onChange={(e) => patch({ pathTemplate: e.target.value })}
                hint="Path portion of the URL. Slot values are URL-encoded automatically."
              />

              <KVEditor
                label="Query parameters"
                hint="Appended to the URL. Values support templating."
                items={state.queryParams}
                onChange={(queryParams) => patch({ queryParams })}
                nameHint="format"
                valueHint="json or {{slots.x}}"
              />

              <KVEditor
                label="Static headers"
                hint="Authorization is set automatically — use the Auth section below."
                items={state.staticHeaders}
                onChange={(staticHeaders) => patch({ staticHeaders })}
                nameHint="Accept"
                valueHint="application/json"
              />

              {state.method === "POST" && (
                <div>
                  <label className="block text-sm font-medium text-gray-900">Body (JSON)</label>
                  <textarea
                    rows={6}
                    placeholder={'{\n  "order_id": "{{slots.order_id}}"\n}'}
                    value={state.bodyTemplate}
                    onChange={(e) => patch({ bodyTemplate: e.target.value })}
                    className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-xs focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Substitutions are JSON-string-escaped. Content-Type is set to application/json.
                  </p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Vars */}
          <Card>
            <CardHeader title="Variables" description="Connector-level constants. Reference as {{vars.X}} in any template field." />
            <CardBody>
              <KVEditor
                label=""
                items={state.vars}
                onChange={(vars) => patch({ vars })}
                nameHint="shop_domain"
                valueHint="acme.myshopify.com"
              />
            </CardBody>
          </Card>

          {/* Auth */}
          <Card>
            <CardHeader title="Authentication" description="API-key header for v1. Bearer / OAuth land in Phase 2." />
            <CardBody className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Header name"
                  placeholder="Authorization"
                  value={state.authHeaderName}
                  onChange={(e) => patch({ authHeaderName: e.target.value })}
                  hint="e.g. Authorization, X-API-Key, X-Shopify-Access-Token"
                />
                <Input
                  label="Value prefix (optional)"
                  placeholder="Bearer "
                  value={state.authHeaderValuePrefix}
                  onChange={(e) => patch({ authHeaderValuePrefix: e.target.value })}
                  hint="Prepended to the secret. Leave empty for raw header value."
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <KeyRound className="h-3.5 w-3.5 text-gray-500" /> Secret value
                </label>
                <input
                  type="password"
                  placeholder={state.hasSecret ? "•••••••• (leave empty to keep existing)" : "Paste your API key"}
                  value={authSecret}
                  onChange={(e) => setAuthSecret(e.target.value)}
                  className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Stored encrypted in DynamoDB with KMS. Tenant-isolated via EncryptionContext. Never returned in any API response.
                  {state.hasSecret && !authSecret && (
                    <> Current secret is preserved. Type a new value to rotate.</>
                  )}
                </p>
              </div>
            </CardBody>
          </Card>

          {/* Response mapping */}
          <Card>
            <CardHeader
              title="Response mapping"
              description="Pick fields from the JSON response and decide whether the AI can share each with the customer."
            />
            <CardBody>
              <ResponseMappingEditor
                items={state.responseMapping}
                onChange={(responseMapping) => patch({ responseMapping })}
              />
            </CardBody>
          </Card>

          {/* Timeout */}
          <Card>
            <CardHeader title="Safety" description="Hard caps on request behavior." />
            <CardBody>
              <Input
                label="Timeout (ms)"
                type="number"
                min={100}
                max={30000}
                value={state.timeoutMs}
                onChange={(e) => patch({ timeoutMs: Math.max(100, Math.min(30000, Number(e.target.value) || 10000)) })}
                hint="Request aborts after this duration. Max 30 seconds."
              />
            </CardBody>
          </Card>

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving}>
              {saving ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="mr-1.5 h-3.5 w-3.5" /> : null}
              {saving ? "Saving" : saved ? "Saved" : isNew ? "Create connector" : "Save changes"}
            </Button>
            {!isNew && !state.hasSecret && !authSecret && (
              <span className="inline-flex items-center gap-1 text-sm text-amber-700">
                <AlertCircle className="h-3.5 w-3.5" /> No secret set — connector won't run.
              </span>
            )}
          </div>
        </div>

        {/* Test panel */}
        {!isNew && state.hasSecret && (
          <div className="lg:sticky lg:top-6 lg:self-start">
            <TestPanel connectorId={id!} slotNames={collectSlotNames(state)} />
          </div>
        )}
        {!isNew && !state.hasSecret && (
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Card>
              <CardBody className="space-y-2 text-sm text-gray-600">
                <p className="font-medium text-gray-900">Test panel</p>
                <p>Save the connector with a secret to enable testing.</p>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── KV editor ─────────────────────────────────────────────────────── */

function KVEditor({
  label, hint, items, onChange, nameHint, valueHint,
}: {
  label: string;
  hint?: string;
  items: KV[];
  onChange: (next: KV[]) => void;
  nameHint?: string;
  valueHint?: string;
}) {
  const add = () => onChange([...items, { name: "", value: "" }]);
  const update = (i: number, patch: Partial<KV>) => onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-900">{label}</label>}
      <div className="mt-1.5 space-y-2">
        {items.length === 0 && (
          <p className="text-xs text-gray-400 italic">None.</p>
        )}
        {items.map((kv, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2">
            <input
              placeholder={nameHint}
              value={kv.name}
              onChange={(e) => update(i, { name: e.target.value })}
              className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs"
            />
            <input
              placeholder={valueHint}
              value={kv.value}
              onChange={(e) => update(i, { value: e.target.value })}
              className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs"
            />
            <button type="button" onClick={() => remove(i)} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/* ── Response mapping editor ───────────────────────────────────────── */

function ResponseMappingEditor({
  items,
  onChange,
}: {
  items: MappingRow[];
  onChange: (next: MappingRow[]) => void;
}) {
  const add = () =>
    onChange([...items, { name: "", path: "", visibility: "internal" }]);
  const update = (i: number, p: Partial<MappingRow>) =>
    onChange(items.map((it, j) => (j === i ? { ...it, ...p } : it)));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));

  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="text-xs text-gray-400 italic">No fields mapped yet.</p>
      )}
      {items.map((row, i) => (
        <div key={i} className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
          <div className="grid grid-cols-[1fr_2fr_auto] gap-2">
            <input
              placeholder="variable_name"
              value={row.name}
              onChange={(e) => update(i, { name: e.target.value })}
              className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs"
            />
            <input
              placeholder="data.order.status"
              value={row.path}
              onChange={(e) => update(i, { path: e.target.value })}
              className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              title="Remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-2">
            <select
              value={row.visibility}
              onChange={(e) => update(i, { visibility: e.target.value as Visibility })}
              className={
                "rounded-md border px-2 py-1.5 text-xs font-medium " +
                (row.visibility === "customer"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-gray-200 bg-gray-50 text-gray-700")
              }
            >
              <option value="internal">Internal — AI sees, does not quote</option>
              <option value="customer">Customer — AI may quote verbatim</option>
            </select>
            <input
              placeholder="Label shown to AI (optional, e.g. 'Order status')"
              value={row.label ?? ""}
              onChange={(e) => update(i, { label: e.target.value || undefined })}
              className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs"
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
      >
        <Plus className="h-3 w-3" /> Add field
      </button>
      <p className="text-xs text-gray-500">
        Default is <strong>Internal</strong> — the AI uses the value to decide what to say but doesn't put it in the customer email. Mark a field <strong>Customer</strong> only when you want the AI to quote its value (e.g. an order status, a tracking number, a date).{" "}
        Internal values are also scanned for leaks: if the AI accidentally quotes one in the reply, the message is held for review.
      </p>
    </div>
  );
}

/* ── Test panel ────────────────────────────────────────────────────── */

interface MappedField {
  value: unknown;
  visibility: Visibility;
  label?: string;
}

interface TestResult {
  ok: boolean;
  /** New shape: each entry carries value + visibility + optional label.
   *  We render customer-facing fields with a green badge, internal with gray. */
  mapped?: Record<string, MappedField>;
  customerFacts?: Record<string, unknown>;
  internalContext?: Record<string, unknown>;
  requestSnapshot?: { method: string; url: string; headers: Record<string, string>; body: string | null };
  responseSnapshot?: { status?: number; durationMs?: number; bodyText?: string; bodyParsed?: unknown; truncated?: boolean };
  reason?: string;
  message?: string;
}

function TestPanel({ connectorId, slotNames }: { connectorId: string; slotNames: string[] }) {
  const [slotValues, setSlotValues] = useState<Record<string, string>>({});
  const [customerEmail, setCustomerEmail] = useState("priya@example.com");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);

  // Reset slot inputs when the connector's referenced slots change.
  useEffect(() => {
    setSlotValues((cur) => {
      const next: Record<string, string> = {};
      for (const name of slotNames) next[name] = cur[name] ?? "";
      return next;
    });
  }, [slotNames.join("|")]);

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.post<TestResult>(`/api/connectors/${connectorId}/test`, {
        slots: slotValues,
        customer: { email: customerEmail, name: "Test Customer" },
        case: { id: "case_test", subject: "Test subject" },
      });
      setResult(res);
    } catch (err) {
      const e = err as ApiError;
      setResult({ ok: false, message: e.message ?? "Test request failed.", reason: e.code });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Test connector" description="Run a real request with sample inputs. Auth header is redacted in the snapshot." />
      <CardBody className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Slots referenced</p>
          {slotNames.length === 0 && (
            <p className="text-xs text-gray-400 italic">No {`{{slots.X}}`} tokens detected in this connector's templates.</p>
          )}
          {slotNames.map((name) => (
            <div key={name} className="grid grid-cols-[100px_1fr] items-center gap-2">
              <code className="text-xs text-gray-600">{name}</code>
              <input
                value={slotValues[name] ?? ""}
                onChange={(e) => setSlotValues((s) => ({ ...s, [name]: e.target.value }))}
                placeholder="value"
                className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs"
              />
            </div>
          ))}
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">{`{{customer.email}}`}</label>
          <input
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs"
          />
        </div>
        <Button onClick={run} disabled={running} className="w-full">
          {running ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
          {running ? "Running" : "Run test"}
        </Button>

        {result && (
          <div className="space-y-3">
            {/* Status banner */}
            {result.ok ? (
              <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                ✓ {result.responseSnapshot?.status} OK in {result.responseSnapshot?.durationMs}ms
              </div>
            ) : (
              <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">
                ✗ {result.reason ?? "failed"} — {result.message}
              </div>
            )}

            {result.mapped && Object.keys(result.mapped).length > 0 && (
              <CollapseBox title="Mapped output (what the AI sees)" defaultOpen>
                <div className="space-y-1.5">
                  {Object.entries(result.mapped).map(([name, field]) => {
                    const customer = field.visibility === "customer";
                    return (
                      <div
                        key={name}
                        className={
                          "rounded-md border px-2 py-1.5 " +
                          (customer
                            ? "border-emerald-200 bg-emerald-50"
                            : "border-gray-200 bg-white")
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <code className="font-mono text-[11px] text-gray-900">{name}</code>
                          <span
                            className={
                              "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase " +
                              (customer
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-gray-100 text-gray-600")
                            }
                          >
                            {customer ? "Customer" : "Internal"}
                          </span>
                        </div>
                        {field.label && (
                          <div className="mt-0.5 text-[10px] text-gray-500">Label: {field.label}</div>
                        )}
                        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] text-gray-800">
                          {JSON.stringify(field.value, null, 2)}
                        </pre>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-gray-500">
                  Green = AI may quote in the customer email. Gray = internal context only;
                  if it accidentally appears in the reply, send is blocked and the case is held
                  for review.
                </p>
              </CollapseBox>
            )}
            {result.requestSnapshot && (
              <CollapseBox title="Request sent (auth redacted)">
                <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[10px] text-gray-700">
{`${result.requestSnapshot.method} ${result.requestSnapshot.url}
${Object.entries(result.requestSnapshot.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}
${result.requestSnapshot.body ? `\n${result.requestSnapshot.body}` : ""}`}
                </pre>
              </CollapseBox>
            )}
            {result.responseSnapshot?.bodyText && (
              <CollapseBox title={`Raw response${result.responseSnapshot.truncated ? " (truncated)" : ""}`}>
                <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[10px] text-gray-700">
                  {result.responseSnapshot.bodyText.slice(0, 2000)}
                </pre>
              </CollapseBox>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function CollapseBox({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-gray-700"
      >
        {title}
        <span className="text-gray-400">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="border-t border-gray-200 px-3 py-2">{children}</div>}
    </div>
  );
}

/* ── helpers ───────────────────────────────────────────────────────── */

/** Convert UI shape (arrays) back to server shape (records). responseMapping
 *  carries the per-field visibility + optional label; vars stays flat. */
function serialize(s: ConnectorState, authSecret: string) {
  const vars: Record<string, string> = {};
  for (const { name, value } of s.vars) if (name.trim()) vars[name.trim()] = value;
  const responseMapping: Record<string, { path: string; visibility: Visibility; label?: string }> = {};
  for (const row of s.responseMapping) {
    if (!row.name.trim() || !row.path.trim()) continue;
    responseMapping[row.name.trim()] = {
      path: row.path,
      visibility: row.visibility,
      ...(row.label?.trim() ? { label: row.label.trim() } : {}),
    };
  }
  return {
    displayName: s.displayName.trim(),
    description: s.description.trim(),
    method: s.method,
    baseUrl: s.baseUrl.trim(),
    pathTemplate: s.pathTemplate,
    queryParams: s.queryParams.filter((kv) => kv.name.trim()),
    staticHeaders: s.staticHeaders.filter((kv) => kv.name.trim()),
    bodyTemplate: s.method === "POST" ? s.bodyTemplate : undefined,
    bodyContentType: s.method === "POST" ? "application/json" : undefined,
    vars,
    authHeaderName: s.authHeaderName.trim(),
    authHeaderValuePrefix: s.authHeaderValuePrefix,
    responseMapping,
    timeoutMs: s.timeoutMs,
    ...(authSecret.trim() ? { authSecret: authSecret.trim() } : {}),
  };
}

/** Walk template fields and pull out every {{slots.X}} token so the test
 *  panel can prompt for exactly those values. */
function collectSlotNames(s: ConnectorState): string[] {
  const RX = /\{\{\s*slots\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const set = new Set<string>();
  const scan = (text: string | undefined) => {
    if (!text) return;
    for (const m of text.matchAll(RX)) set.add(m[1]!);
  };
  scan(s.pathTemplate);
  scan(s.bodyTemplate);
  for (const kv of s.queryParams) scan(kv.value);
  for (const kv of s.staticHeaders) scan(kv.value);
  return [...set].sort();
}
